import { getStoredConsent } from './consent-storage';
import { movieGenres, tvGenres } from './genres-for-filters';
import { getLanguageFromPath } from './languages';

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

export type ContentFormat = 'movies' | 'tv';
export type Placement = 'wheel' | 'slider';

type GenreOption = { id: string; en: string };

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function stripUndefined(params: AnalyticsParams): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string | number | boolean] => entry[1] !== undefined)
  );
}

function defaultContext(): AnalyticsParams {
  if (typeof window === 'undefined') return {};
  return {
    locale: getLanguageFromPath(window.location.pathname),
  };
}

export function canSendAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  if (!getStoredConsent()?.analytics) return false;
  return typeof window.gtag === 'function';
}

/**
 * Sends a GA4 event only if the user has granted analytics storage.
 * Use instead of calling window.gtag directly.
 */
export function trackEvent(action: string, params: AnalyticsParams = {}): void {
  if (!canSendAnalytics() || !window.gtag) return;
  window.gtag('event', action, stripUndefined({ ...defaultContext(), ...params }));
}

export function setAnalyticsUserProperties(properties: AnalyticsParams): void {
  if (!canSendAnalytics() || !window.gtag) return;
  const cleaned = stripUndefined(properties);
  if (Object.keys(cleaned).length === 0) return;
  window.gtag('set', 'user_properties', cleaned);
  window.gtag('set', cleaned);
}

export function trackConsentGrantedPageView(): void {
  if (!canSendAnalytics() || !window.gtag) return;
  window.gtag('event', 'page_view', stripUndefined({
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    ...defaultContext(),
  }));
}

export function genreNameFromId(genreId: string | number | undefined, videoFormat: string): string {
  const list = (videoFormat === 'tv' ? tvGenres : movieGenres) as GenreOption[];
  const match = list.find((genre) => genre.id === String(genreId ?? '0'));
  return match?.en ?? 'All Genres';
}

function rouletteParams(opts: {
  videoFormat?: string;
  selectedGenreID?: string | number;
  quality?: string;
  region?: string;
}): AnalyticsParams {
  return {
    content_type: opts.videoFormat,
    genre: opts.videoFormat ? genreNameFromId(opts.selectedGenreID, opts.videoFormat) : undefined,
    genre_id: opts.selectedGenreID !== undefined ? String(opts.selectedGenreID) : undefined,
    quality: opts.quality,
    watch_region: opts.region,
  };
}

export function trackSpinWheel(opts: {
  videoFormat: string;
  selectedGenreID: string | number;
  quality: string;
  region?: string;
  spinCount: number;
}): void {
  trackEvent('spin_wheel', {
    ...rouletteParams(opts),
    spin_count: opts.spinCount,
  });
}

export function trackViewRecommendation(opts: {
  videoFormat: string;
  selectedGenreID: string | number;
  quality: string;
  region?: string;
  itemId?: string;
  itemName?: string;
  spinCount?: number;
}): void {
  trackEvent('view_recommendation', {
    ...rouletteParams(opts),
    item_id: opts.itemId,
    item_name: opts.itemName,
    spin_count: opts.spinCount,
  });
}

export function trackWatchNetflix(opts: {
  itemName: string;
  itemId?: string;
  placement: Placement;
  videoFormat?: string;
  selectedGenreID?: string | number;
  quality?: string;
  region?: string;
  listName?: string;
  linkUrl?: string;
}): void {
  trackEvent('click_watch_netflix', {
    ...rouletteParams(opts),
    item_id: opts.itemId,
    item_name: opts.itemName,
    placement: opts.placement,
    item_list_name: opts.listName,
    link_url: opts.linkUrl,
  });
}

export function trackTrailer(opts: {
  itemName: string;
  placement: Placement;
  method: 'modal' | 'youtube_search';
  videoFormat?: string;
  selectedGenreID?: string | number;
  quality?: string;
  region?: string;
  listName?: string;
}): void {
  trackEvent('click_trailer', {
    ...rouletteParams(opts),
    item_name: opts.itemName,
    placement: opts.placement,
    method: opts.method,
    item_list_name: opts.listName,
  });
}

export function trackSelectFilter(opts: {
  filterName: 'genre' | 'format' | 'quality' | 'region';
  filterValue: string;
  videoFormat?: string;
}): void {
  trackEvent('select_content', {
    content_type: 'filter',
    item_id: opts.filterName,
    item_name: opts.filterValue,
    video_format: opts.videoFormat,
  });
}

export function trackGenerateLead(formName = 'contact'): void {
  trackEvent('generate_lead', { form_name: formName });
}

export function trackException(description: string, extra: AnalyticsParams = {}): void {
  trackEvent('exception', {
    description,
    fatal: false,
    ...extra,
  });
}
